import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ListItem } from '@/components/ui/ListItem';
import { Spinner } from '@/components/ui/Spinner';
import { usePaymentMethods } from '@/api/queries/payments';
import { useSubscribe, useSubscriptionPlans } from '@/api/queries/subscription';
import { centsToUsdString } from '@/utils/money';
import { useLocale } from '@/hooks/useLocale';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';
import { isAPIError } from '@/api/error';
import { IcPlus } from '@/components/ui/icons';

export function SubscribePaymentPage(): JSX.Element {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { planCode = '' } = useParams<{ planCode: string }>();
  const navigate = useNavigate();
  const plans = useSubscriptionPlans(true);
  const pms = usePaymentMethods();
  const subscribe = useSubscribe();
  const pushToast = useUIStore((s) => s.pushToast);
  const [selected, setSelected] = useState<string | null>(null);

  const plan = plans.data?.find((p) => p.code === planCode);

  const handleBuy = async () => {
    if (!plan) return;
    try {
      const intent = await subscribe.mutateAsync({
        planCode: plan.code,
        paymentMethodId: selected ?? undefined,
      });
      if (intent.payment_page_url || intent.widget_config) {
        navigate(paths.payIntent(intent.intent_id));
        return;
      }
      if (intent.checkout_url) {
        window.open(intent.checkout_url, '_blank', 'noopener');
      }
      navigate(paths.subscribeSuccess);
    } catch (err) {
      const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
      pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
      navigate(paths.subscribeError);
    }
  };

  // Addendum Q9: Apple/Google Pay stub buttons — UI only.
  const initiateStubWallet = (provider: 'applepay' | 'googlepay') => {
    pushToast({
      body: `${provider === 'applepay' ? 'Apple' : 'Google'} Pay (stub) — opening checkout…`,
      variant: 'info',
    });
    handleBuy();
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('subscribe.title')} />
        {plans.isLoading && <Spinner />}
        {plan && (
          <Card variant="light" padding="md">
            <div style={{ fontWeight: 600 }}>{plan.code}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {centsToUsdString(plan.amount_cents, locale)}
            </div>
          </Card>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Button block variant="primary" onClick={() => initiateStubWallet('applepay')}>
            {t('subscribe.apple_pay')}
          </Button>
          <Button block variant="secondary" onClick={() => initiateStubWallet('googlepay')}>
            {t('subscribe.google_pay')}
          </Button>
        </div>

        <h3 style={{ marginBlock: 8 }}>{t('subscribe.select_card')}</h3>
        {pms.isLoading && <Spinner />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(pms.data ?? []).map((p) => (
            <ListItem
              key={p.id}
              title={`${p.brand.toUpperCase()} •••• ${p.last4}`}
              subtitle={`${String(p.expiry_month).padStart(2, '0')}/${p.expiry_year}`}
              trailing={selected === p.id ? '✓' : ''}
              onClick={() => setSelected(p.id)}
            />
          ))}
        </div>
        <Button block variant="ghost" startIcon={<IcPlus />} onClick={() => navigate(paths.subscribeAddCard)}>
          {t('subscribe.add_card')}
        </Button>

        {plan && (
          <Button block size="lg" variant="primary" onClick={handleBuy} loading={subscribe.isPending}>
            {t('subscribe.buy_for', { price: centsToUsdString(plan.amount_cents, locale) })}
          </Button>
        )}
      </PageContainer>
    </AppShell>
  );
}
